import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "sonner";
import { FileText, Plus, Search, Eye, ArrowRight, Filter } from "lucide-react";
import AdvancedFilters from "../components/AdvancedFilters";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BilansPage() {
  const { isAdmin, isCabinet } = useAuth();
  const [searchParams] = useSearchParams();
  const [bilans, setBilans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "all");
  const [yearFilter, setYearFilter] = useState("all");
  const [advancedFilters, setAdvancedFilters] = useState({});

  useEffect(() => {
    fetchBilans();
  }, []);

  const fetchBilans = async () => {
    try {
      const response = await axios.get(`${API_URL}/bilans`);
      setBilans(response.data);
    } catch (error) {
      console.error(/* PROD */ "Failed to fetch bilans:", error);
      toast.error("Erreur lors du chargement des bilans");
    } finally {
      setLoading(false);
    }
  };

  const handleAdvancedFiltersChange = (filters) => {
    setAdvancedFilters(filters);
    // Synchroniser avec les filtres existants
    if (filters.statut !== "all") setStatusFilter(filters.statut);
    if (filters.annee !== "all") setYearFilter(filters.annee);
    if (filters.search) setSearchTerm(filters.search);
  };

  const filteredBilans = bilans.filter((bilan) => {
    const matchesSearch =
      (bilan.enterprise_name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (bilan.enterprise_name || "").toLowerCase().includes(advancedFilters.search?.toLowerCase() || "");
    const matchesStatus = statusFilter === "all" || bilan.status === statusFilter;
    const matchesYear = yearFilter === "all" || bilan.annee.toString() === yearFilter;
    
    // Filtres avancés
    const matchesRegion = !advancedFilters.region || advancedFilters.region === "all" || 
      (bilan.enterprise_region || "").toLowerCase() === advancedFilters.region?.toLowerCase();
    const matchesSecteur = !advancedFilters.secteur || advancedFilters.secteur === "all" || 
      (bilan.enterprise_secteur || "").includes(advancedFilters.secteur);
    const matchesCabinet = !advancedFilters.cabinet_id || advancedFilters.cabinet_id === "all" || 
      bilan.cabinet_id === advancedFilters.cabinet_id;
    
    return matchesSearch && matchesStatus && matchesYear && matchesRegion && matchesSecteur && matchesCabinet;
  });

  const years = [...new Set(bilans.map((b) => b.annee))].sort((a, b) => b - a);

  const getStatusBadge = (status) => {
    const badges = {
      brouillon: "badge-neutral",
      soumis: "badge-warning",
      en_controle: "badge-warning",
      valide: "badge-success",
      rejete: "badge-error",
    };
    const labels = {
      brouillon: "Brouillon",
      soumis: "Soumis",
      en_controle: "En contrôle",
      valide: "Validé",
      rejete: "Rejeté",
    };
    return <span className={badges[status] || "badge-neutral"}>{labels[status] || status}</span>;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-40" />
        </div>
        <Card>
          <CardContent className="p-6">
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Calculate statistics
  const stats = {
    total: bilans.length,
    brouillon: bilans.filter(b => b.status === "brouillon").length,
    soumis: bilans.filter(b => b.status === "soumis").length,
    en_controle: bilans.filter(b => b.status === "en_controle").length,
    valide: bilans.filter(b => b.status === "valide").length,
    rejete: bilans.filter(b => b.status === "rejete").length,
  };
  const pendingCount = stats.soumis + stats.en_controle;

  return (
    <div className="space-y-6" data-testid="bilans-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Bilans Sociaux</h1>
          <p className="text-muted-foreground mt-1">
            {isAdmin ? "Tous les bilans déclarés" : "Vos déclarations de bilans sociaux"}
          </p>
        </div>
        {isCabinet && (
          <Link to="/bilans/nouveau">
            <Button className="bg-primary hover:bg-primary/90" data-testid="create-bilan-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nouveau Bilan
            </Button>
          </Link>
        )}
      </div>

      {/* Admin Statistics Cards */}
      {isAdmin && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card 
            className={`cursor-pointer transition-all ${statusFilter === "soumis" ? "ring-2 ring-amber-500" : "hover:shadow-md"}`}
            onClick={() => setStatusFilter(statusFilter === "soumis" ? "all" : "soumis")}
          >
            <CardContent className="p-4">
              <div className="text-amber-600 text-sm font-medium">En attente</div>
              <div className="text-3xl font-bold text-amber-700">{stats.soumis}</div>
            </CardContent>
          </Card>
          <Card 
            className={`cursor-pointer transition-all ${statusFilter === "en_controle" ? "ring-2 ring-orange-500" : "hover:shadow-md"}`}
            onClick={() => setStatusFilter(statusFilter === "en_controle" ? "all" : "en_controle")}
          >
            <CardContent className="p-4">
              <div className="text-orange-600 text-sm font-medium">En contrôle</div>
              <div className="text-3xl font-bold text-orange-700">{stats.en_controle}</div>
            </CardContent>
          </Card>
          <Card 
            className={`cursor-pointer transition-all ${statusFilter === "valide" ? "ring-2 ring-emerald-500" : "hover:shadow-md"}`}
            onClick={() => setStatusFilter(statusFilter === "valide" ? "all" : "valide")}
          >
            <CardContent className="p-4">
              <div className="text-emerald-600 text-sm font-medium">Validés</div>
              <div className="text-3xl font-bold text-emerald-700">{stats.valide}</div>
            </CardContent>
          </Card>
          <Card 
            className={`cursor-pointer transition-all ${statusFilter === "rejete" ? "ring-2 ring-red-500" : "hover:shadow-md"}`}
            onClick={() => setStatusFilter(statusFilter === "rejete" ? "all" : "rejete")}
          >
            <CardContent className="p-4">
              <div className="text-red-600 text-sm font-medium">Rejetés</div>
              <div className="text-3xl font-bold text-red-700">{stats.rejete}</div>
            </CardContent>
          </Card>
          <Card 
            className={`cursor-pointer transition-all ${statusFilter === "all" ? "ring-2 ring-primary" : "hover:shadow-md"}`}
            onClick={() => setStatusFilter("all")}
          >
            <CardContent className="p-4">
              <div className="text-slate-600 text-sm font-medium">Total</div>
              <div className="text-3xl font-bold text-slate-700">{stats.total}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="space-y-4">
            {/* Filtres basiques */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Rechercher par entreprise..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-bilans"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-48">
                  <SelectValue placeholder="Filtrer par statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="brouillon">Brouillon</SelectItem>
                  <SelectItem value="soumis">Soumis</SelectItem>
                  <SelectItem value="en_controle">En contrôle</SelectItem>
                  <SelectItem value="valide">Validé</SelectItem>
                  <SelectItem value="rejete">Rejeté</SelectItem>
                </SelectContent>
              </Select>
              <Select value={yearFilter} onValueChange={setYearFilter}>
                <SelectTrigger className="w-full sm:w-36">
                  <SelectValue placeholder="Année" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes</SelectItem>
                  {years.map((year) => (
                    <SelectItem key={year} value={year.toString()}>
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Filtres avancés (Admin uniquement) */}
            {isAdmin && (
              <AdvancedFilters
                onFiltersChange={handleAdvancedFiltersChange}
                showSearch={false}
                showStatut={false}
                showPeriode={false}
                showRegion={true}
                showSecteur={true}
                showCabinet={true}
              />
            )}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {filteredBilans.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="text-muted-foreground">Aucun bilan trouvé</p>
              {isCabinet && (
                <Link to="/bilans/nouveau">
                  <Button className="mt-4">
                    <Plus className="w-4 h-4 mr-2" />
                    Créer un bilan
                  </Button>
                </Link>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Entreprise</TableHead>
                  <TableHead>Année</TableHead>
                  <TableHead>Région</TableHead>
                  <TableHead>Date création</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredBilans.map((bilan) => (
                  <TableRow key={bilan.id}>
                    <TableCell>
                      <p className="font-medium">{bilan.enterprise_name || "N/A"}</p>
                    </TableCell>
                    <TableCell>
                      <span className="font-mono">{bilan.annee}</span>
                    </TableCell>
                    <TableCell>{bilan.region || "N/A"}</TableCell>
                    <TableCell>
                      {new Date(bilan.created_at).toLocaleDateString("fr-FR")}
                    </TableCell>
                    <TableCell>{getStatusBadge(bilan.status)}</TableCell>
                    <TableCell className="text-right">
                      <Link to={`/bilans/${bilan.id}`}>
                        <Button variant="ghost" size="sm">
                          <Eye className="w-4 h-4 mr-2" />
                          Voir
                        </Button>
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
